import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

function generateChapters(count: number, status: string): { num: number; title: string; date: string }[] {
  const chapters = [];
  const total = Math.min(count, 300);
  for (let i = total; i >= 1; i--) {
    chapters.push({
      num: i,
      title: `第${i}话`,
      date: `2026-${String(Math.max(1, Math.min(12, 7 - Math.floor((total - i) / 30)))).padStart(2, '0')}-${String(Math.max(1, Math.min(28, 28 - ((total - i) % 28)))).padStart(2, '0')}`,
    });
  }
  return chapters;
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-colors whitespace-nowrap text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = generateChapters(comic.chapterCount, comic.status);
  const relatedComics = comics.filter(c => c.id !== comic.id && c.categorySlug === comic.categorySlug).slice(0, 6);
  const latestChapters = chapters.slice(-10).reverse();

  const comicSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": `${comic.title} - 韩漫画网`,
        "description": comic.description,
        "author": { "@type": "Person", "name": comic.author },
        "datePublished": "2025-01-01",
        "dateModified": comic.updatedAt,
        "image": comic.coverImage,
        "publisher": { "@type": "Organization", "name": "韩漫画网", "url": "http://coffeemate.cn/" },
        "mainEntityOfPage": { "@type": "WebPage", "@id": `http://coffeemate.cn/comic/${comic.id}` }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://coffeemate.cn/" },
          { "@type": "ListItem", "position": 2, "name": `${comic.category}韩漫`, "item": `http://coffeemate.cn/category/${comic.categorySlug}` },
          { "@type": "ListItem", "position": 3, "name": comic.title, "item": `http://coffeemate.cn/comic/${comic.id}` }
        ]
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comicSchema) }} />
      <Navbar />

      <main className="pt-16">
        <section className="relative w-full min-h-[360px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩漫画网`}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-black text-background-50 font-heading">{comic.title}</h1>
            <p className="mt-3 text-sm text-background-300">
              {comic.author} · {comic.category} · <span className={comic.status === '连载中' ? 'text-primary-400' : 'text-accent-400'}>{comic.status}</span>
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">{comic.category}韩漫</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <button className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer">
                    <i className="ri-play-circle-line mr-1.5"></i>开始阅读
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <span className="text-2xl font-bold text-foreground-900">{comic.rating}</span>
                    <i className="ri-star-fill text-amber-400"></i>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    <span>{comic.views}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-file-list-3-line"></i>
                    <span>共{comic.chapterCount}话</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">最新章节</h3>
                  <div className="flex flex-col gap-2">
                    {latestChapters.map((ch) => (
                      <Link
                        key={ch.num}
                        to={`/comic/${comic.id}`}
                        className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-background-50 transition-colors group"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                          {ch.title}
                        </span>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">全部章节</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {chapters.map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.id}`}
                  className="block py-2.5 px-3 text-center text-sm text-foreground-700 bg-background-50 rounded-md hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  {ch.title}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}